// Capa de abstracción de base de datos
// Usa IndexedDB con estructura relacional, preparado para migración a Supabase

class Database {
  constructor() {
    this.dbName = 'BilboTrainingDB';
    this.version = 2; // Incrementado para añadir training_series
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Tabla de usuarios
        if (!db.objectStoreNames.contains('users')) {
          const usersStore = db.createObjectStore('users', { keyPath: 'id', autoIncrement: true });
          usersStore.createIndex('username', 'username', { unique: true });
        }

        // Tabla de progresiones
        if (!db.objectStoreNames.contains('progressions')) {
          const progressionsStore = db.createObjectStore('progressions', { keyPath: 'id', autoIncrement: true });
          progressionsStore.createIndex('user_id', 'user_id');
          progressionsStore.createIndex('status', 'status');
        }

        // Tabla de entrenamientos
        if (!db.objectStoreNames.contains('trainings')) {
          const trainingsStore = db.createObjectStore('trainings', { keyPath: 'id', autoIncrement: true });
          trainingsStore.createIndex('progression_id', 'progression_id');
          trainingsStore.createIndex('date', 'date');
        }

        // Tabla de series adicionales del entrenamiento (nueva en versión 2)
        if (!db.objectStoreNames.contains('training_series')) {
          const trainingSeriesStore = db.createObjectStore('training_series', { keyPath: 'id', autoIncrement: true });
          trainingSeriesStore.createIndex('training_id', 'training_id');
        }

        // Tabla de ejercicios del entrenamiento
        if (!db.objectStoreNames.contains('training_exercises')) {
          const trainingExercisesStore = db.createObjectStore('training_exercises', { keyPath: 'id', autoIncrement: true });
          trainingExercisesStore.createIndex('training_id', 'training_id');
        }

        // Tabla de récords de press banca
        if (!db.objectStoreNames.contains('bench_press_records')) {
          const recordsStore = db.createObjectStore('bench_press_records', { keyPath: 'id', autoIncrement: true });
          recordsStore.createIndex('user_id', 'user_id');
          recordsStore.createIndex('weight', 'weight');
        }
      };
    });
  }

  // ========== USUARIOS ==========
  async createUser(username, passwordHash) {
    const transaction = this.db.transaction(['users'], 'readwrite');
    const store = transaction.objectStore('users');
    return new Promise((resolve, reject) => {
      const request = store.add({
        username,
        password_hash: passwordHash,
        created_at: new Date().toISOString()
      });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getUserByUsername(username) {
    const transaction = this.db.transaction(['users'], 'readonly');
    const store = transaction.objectStore('users');
    const index = store.index('username');
    return new Promise((resolve, reject) => {
      const request = index.get(username);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ========== PROGRESIONES ==========
  async createProgression(userId, rm, increment, startWeight) {
    const transaction = this.db.transaction(['progressions'], 'readwrite');
    const store = transaction.objectStore('progressions');
    return store.add({
      user_id: userId,
      rm: rm,
      increment: increment,
      start_weight: startWeight,
      current_weight: startWeight,
      status: 'active',
      started_at: new Date().toISOString(),
      created_at: new Date().toISOString()
    });
  }

  async getActiveProgression(userId) {
    const transaction = this.db.transaction(['progressions'], 'readonly');
    const store = transaction.objectStore('progressions');
    const index = store.index('user_id');
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(userId));
      const progressions = [];
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.status === 'active') {
            progressions.push(cursor.value);
          }
          cursor.continue();
        } else {
          resolve(progressions.length > 0 ? progressions[0] : null);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async getAllProgressions(userId) {
    const transaction = this.db.transaction(['progressions'], 'readonly');
    const store = transaction.objectStore('progressions');
    const index = store.index('user_id');
    return new Promise((resolve, reject) => {
      const request = index.getAll(userId);
      request.onsuccess = () => {
        const progressions = request.result.sort((a, b) => 
          new Date(b.started_at) - new Date(a.started_at)
        );
        resolve(progressions);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async updateProgression(progressionId, updates) {
    const transaction = this.db.transaction(['progressions'], 'readwrite');
    const store = transaction.objectStore('progressions');
    const progression = await new Promise((resolve, reject) => {
      const request = store.get(progressionId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!progression) throw new Error('Progression not found');

    Object.assign(progression, updates);
    return store.put(progression);
  }

  async completeProgression(progressionId) {
    return this.updateProgression(progressionId, {
      status: 'completed',
      completed_at: new Date().toISOString()
    });
  }

  // ========== ENTRENAMIENTOS ==========
  async createTraining(progressionId, weight, series1Reps, additionalSeries, comments) {
    const transaction = this.db.transaction(['trainings', 'training_series'], 'readwrite');
    const trainingsStore = transaction.objectStore('trainings');
    const seriesStore = transaction.objectStore('training_series');
    
    return new Promise((resolve, reject) => {
      // Crear el entrenamiento
      const trainingRequest = trainingsStore.add({
        progression_id: progressionId,
        date: new Date().toISOString(),
        series1_reps: series1Reps,
        comments: comments || null,
        created_at: new Date().toISOString()
      });

      trainingRequest.onsuccess = () => {
        const trainingId = trainingRequest.result;
        
        // Guardar las series adicionales
        if (additionalSeries && additionalSeries.length > 0) {
          let seriesCount = 0;
          additionalSeries.forEach((series, index) => {
            const seriesRequest = seriesStore.add({
              training_id: trainingId,
              series_number: index + 2, // Empieza en 2 (la 1 es la Bilbo)
              weight: series.weight,
              reps: series.reps,
              created_at: new Date().toISOString()
            });
            
            seriesRequest.onsuccess = () => {
              seriesCount++;
              if (seriesCount === additionalSeries.length) {
                resolve(trainingId);
              }
            };
            
            seriesRequest.onerror = () => reject(seriesRequest.error);
          });
        } else {
          resolve(trainingId);
        }
      };

      trainingRequest.onerror = () => reject(trainingRequest.error);
    });
  }

  async getTrainingsByProgression(progressionId) {
    const transaction = this.db.transaction(['trainings', 'training_series'], 'readonly');
    const trainingsStore = transaction.objectStore('trainings');
    const index = trainingsStore.index('progression_id');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(progressionId);
      request.onsuccess = async () => {
        const trainings = request.result.sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        
        // Cargar series adicionales para cada entrenamiento
        const trainingsWithSeries = await Promise.all(
          trainings.map(async (training) => {
            const series = await this.getSeriesByTraining(training.id);
            // Obtener el peso de la primera serie adicional (si existe) para compatibilidad
            const weight = series.length > 0 ? series[0].weight : null;
            return { ...training, weight, additionalSeries: series };
          })
        );
        
        resolve(trainingsWithSeries);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getSeriesByTraining(trainingId) {
    const transaction = this.db.transaction(['training_series'], 'readonly');
    const store = transaction.objectStore('training_series');
    const index = store.index('training_id');
    return new Promise((resolve, reject) => {
      const request = index.getAll(trainingId);
      request.onsuccess = () => {
        const series = request.result.sort((a, b) => a.series_number - b.series_number);
        resolve(series);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getAllTrainings(userId) {
    const progressions = await this.getAllProgressions(userId);
    const progressionIds = progressions.map(p => p.id);
    
    const allTrainings = [];
    for (const progressionId of progressionIds) {
      const trainings = await this.getTrainingsByProgression(progressionId);
      allTrainings.push(...trainings);
    }
    
    // Cargar series adicionales para cada entrenamiento
    const trainingsWithSeries = await Promise.all(
      allTrainings.map(async (training) => {
        const series = await this.getSeriesByTraining(training.id);
        return { ...training, additionalSeries: series };
      })
    );
    
    return trainingsWithSeries.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  // ========== EJERCICIOS DEL ENTRENAMIENTO ==========
  async createTrainingExercise(trainingId, exerciseName, weight, reps) {
    const transaction = this.db.transaction(['training_exercises'], 'readwrite');
    const store = transaction.objectStore('training_exercises');
    return store.add({
      training_id: trainingId,
      exercise_name: exerciseName,
      weight: weight,
      reps: reps,
      created_at: new Date().toISOString()
    });
  }

  async getExercisesByTraining(trainingId) {
    const transaction = this.db.transaction(['training_exercises'], 'readonly');
    const store = transaction.objectStore('training_exercises');
    const index = store.index('training_id');
    return new Promise((resolve, reject) => {
      const request = index.getAll(trainingId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ========== RÉCORDS DE PRESS BANCA ==========
  async createBenchPressRecord(userId, weight, reps) {
    const transaction = this.db.transaction(['bench_press_records'], 'readwrite');
    const store = transaction.objectStore('bench_press_records');
    return store.add({
      user_id: userId,
      weight: weight,
      reps: reps,
      date: new Date().toISOString(),
      created_at: new Date().toISOString()
    });
  }

  async updateBenchPressRecord(recordId, weight, reps) {
    const transaction = this.db.transaction(['bench_press_records'], 'readwrite');
    const store = transaction.objectStore('bench_press_records');
    const record = await new Promise((resolve, reject) => {
      const request = store.get(recordId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!record) throw new Error('Record not found');

    record.weight = weight;
    record.reps = reps;
    return store.put(record);
  }

  async deleteBenchPressRecord(recordId) {
    const transaction = this.db.transaction(['bench_press_records'], 'readwrite');
    const store = transaction.objectStore('bench_press_records');
    return store.delete(recordId);
  }

  async getBenchPressRecords(userId) {
    const transaction = this.db.transaction(['bench_press_records'], 'readonly');
    const store = transaction.objectStore('bench_press_records');
    const index = store.index('user_id');
    return new Promise((resolve, reject) => {
      const request = index.getAll(userId);
      request.onsuccess = () => {
        const records = request.result.sort((a, b) => {
          if (b.weight !== a.weight) return b.weight - a.weight;
          return b.reps - a.reps;
        });
        resolve(records);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getBenchPressRecordByWeight(userId, weight) {
    const transaction = this.db.transaction(['bench_press_records'], 'readonly');
    const store = transaction.objectStore('bench_press_records');
    const index = store.index('user_id');
    return new Promise((resolve, reject) => {
      const request = index.getAll(userId);
      request.onsuccess = () => {
        // Buscar récord con el mismo peso (comparación con tolerancia para decimales)
        const record = request.result.find(r => Math.abs(r.weight - weight) < 0.01);
        resolve(record || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async upsertBenchPressRecord(userId, weight, reps) {
    // Buscar si ya existe un récord con ese peso
    const existingRecord = await this.getBenchPressRecordByWeight(userId, weight);
    
    if (existingRecord) {
      // Si existe y las nuevas repeticiones son mayores, actualizar
      if (reps > existingRecord.reps) {
        return this.updateBenchPressRecord(existingRecord.id, weight, reps);
      }
      // Si las repeticiones son menores o iguales, no hacer nada
      return existingRecord.id;
    } else {
      // Si no existe, crear uno nuevo
      return this.createBenchPressRecord(userId, weight, reps);
    }
  }
}

// Instancia singleton
const db = new Database();

export default db;
